import { useState, useRef } from 'react';
import { compressImageToBase64, validateImageFile } from '../utils/imageCompression';

interface CompanyLogoUploadProps {
  currentLogo?: string | null;
  onLogoChange: (base64: string | null) => void;
  disabled?: boolean;
}

export default function CompanyLogoUpload({
  currentLogo,
  onLogoChange,
  disabled = false
}: CompanyLogoUploadProps) {
  const [preview, setPreview] = useState<string | null>(currentLogo || null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);

    // Validate file
    const validationError = validateImageFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setIsUploading(true);
      
      // Compress and convert to base64
      const base64 = await compressImageToBase64(file, 320);
      
      setPreview(base64);
      onLogoChange(base64);
    } catch (err) {
      setError('Gagal memproses gambar');
      console.error('Image compression error:', err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveLogo = () => {
    setPreview(null);
    onLogoChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        Logo Company
      </label>
      
      <div className="flex items-start gap-4">
        {/* Preview */}
        <div className="flex-shrink-0">
          {preview ? (
            <div className="relative w-32 h-32 border-2 border-gray-300 rounded-lg overflow-hidden bg-white">
              <img
                src={preview}
                alt="Company Logo"
                className="w-full h-full object-contain"
              />
            </div>
          ) : (
            <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
              <svg
                className="w-12 h-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
          )}
        </div>

        {/* Upload Controls */}
        <div className="flex-1 space-y-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={handleFileSelect}
            disabled={disabled || isUploading}
            className="hidden"
          />
          
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleClick}
              disabled={disabled || isUploading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm"
            >
              {isUploading ? 'Memproses...' : preview ? 'Ganti Logo' : 'Upload Logo'}
            </button>
            
            {preview && (
              <button
                type="button"
                onClick={handleRemoveLogo}
                disabled={disabled || isUploading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm"
              >
                Hapus
              </button>
            )}
          </div>
          
          <p className="text-xs text-gray-500">
            Format: JPG, PNG, WebP (Max 5MB)
            <br />
            Gambar akan otomatis diresize ke 320x320px
          </p>
          
          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}
        </div>
      </div>
    </div>
  );
}
